/* eslint-disable no-console */
/**
 * Centralized application logger.
 *
 * Diagnostic output (`debug`, `log`, `info`, `time`, `timeEnd`) is emitted only
 * in development and test environments so production bundles stay silent.
 * `warn` and `error` are always forwarded to the console because they signal
 * conditions that operators need to see regardless of environment.
 *
 * Use this instead of calling `console.*` directly in `src/`.
 */

type LogArgs = unknown[];

const isVerboseEnvironment = (): boolean => {
  const env = import.meta.env;
  if (!env) return false;
  return Boolean(env.DEV) || env.MODE === 'test';
};

const verbose = isVerboseEnvironment();

const noop = (..._args: LogArgs): void => {};

export const logger = {
  /** Whether verbose (non-warn/error) output is currently enabled. */
  enabled: verbose,

  debug: verbose ? (...args: LogArgs): void => console.debug(...args) : noop,
  log: verbose ? (...args: LogArgs): void => console.log(...args) : noop,
  info: verbose ? (...args: LogArgs): void => console.info(...args) : noop,

  warn: (...args: LogArgs): void => console.warn(...args),
  error: (...args: LogArgs): void => console.error(...args),

  time: verbose ? (label: string): void => console.time(label) : noop,
  timeEnd: verbose ? (label: string): void => console.timeEnd(label) : noop,
};

export type Logger = typeof logger;

export default logger;
